/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import AnimatedNumber from './AnimatedNumber';
import { MONTH_VIEW_WINDOW_DAYS } from '../../utils/monthViewWindow';

const Table = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Row = styled('div')`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  ${(props) => !props.isHeader && `border-bottom: 1px solid ${props.theme.colors.surfaceSecondary};`}
`;

const HeaderCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  justify-content: ${(props) => (props.alignEnd ? 'flex-end' : 'flex-start')};
  :hover {
    opacity: 0.7;
  }
`;

const DataCell = styled('div')`
  display: flex;
  justify-content: ${(props) => (props.alignEnd ? 'flex-end' : 'flex-start')};
`;

const EmptyState = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 24px 8px;
  text-align: center;
`;

const COLUMNS = [
  { key: 'name', label: 'Tag Name', alignEnd: false },
  { key: 'reps', label: 'Reps', alignEnd: true },
  { key: 'exercises', label: 'Exercises', alignEnd: true },
];

/** Tag Breakdown table. Aggregates the reps and exercise count per tag across the days currently
 * displayed (single day in day view, the displayed week in week view, the 10-week window in month
 * view). An exercise contributes its repCount and a +1 exercise count to every tag it carries.
 *
 * `selectedTagDay` narrows week view to a single day (the sidebar's "Daily" mode): `{ date }` for a
 * calendar week, `{ dayNumber }` for a free-form program week. Undefined/null keeps the full-week
 * aggregate ("Total" mode) and has no effect outside week view. */
const StatsTagBreakdown = ({ selectedTagDay }) => {
  const { colors } = useTheme();
  const [sortColumn, setSortColumn] = useState('reps');
  const [sortDirection, setSortDirection] = useState('desc');

  const programType = useSelector((state) => state.calendar.data.programType);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const currentlyDisplayedWeekdays = useSelector(
    (state) => state.calendar.data.currentlyDisplayedWeekdays,
  );
  const currentlyDisplayedProgramDays = useSelector(
    (state) => state.calendar.data.currentlyDisplayedProgramDays,
  );
  const calendarItems = useSelector((state) => state.calendar.data.calendarItems);
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const monthViewWindowStart = useSelector(
    (state) => state.calendar.data.monthViewWindowStart,
  );
  const tagsForAccount = useSelector((state) => state.calendar.data.tagsForAccount);

  const isProgram = programType === 'free-form-program';

  /** Resolve the day objects to aggregate over, keyed by the current view. Day view relies on
   * selectedDate / selectedProgramDayNumber because currentlyDisplayed* is only populated in week
   * view. Month view walks the full 10-week rolling window (same math as StatsSideBarContent). */
  const displayedDays = useMemo(() => {
    if (viewType === 'dayView') {
      if (isProgram) {
        return [nonDateBasedProgramItems[selectedProgramDayNumber]];
      }
      if (!selectedDate) return [];
      return [calendarItems[moment(selectedDate).format('YYYY-MM-DD')]];
    }
    if (viewType === 'monthView' && !isProgram) {
      if (!monthViewWindowStart) return [];
      const start = moment(monthViewWindowStart, 'YYYY-MM-DD').startOf('week');
      const days = [];
      for (let d = 0; d < MONTH_VIEW_WINDOW_DAYS; d += 1) {
        days.push(calendarItems[start.clone().add(d, 'days').format('YYYY-MM-DD')]);
      }
      return days;
    }
    // weekView (and free-form program, which has no month view).
    // "Daily" mode narrows the week to the single day the sidebar selected.
    if (selectedTagDay) {
      return isProgram
        ? [nonDateBasedProgramItems[selectedTagDay.dayNumber]]
        : [calendarItems[selectedTagDay.date]];
    }
    if (isProgram) {
      return currentlyDisplayedProgramDays.map(
        ({ dayNumber }) => nonDateBasedProgramItems[dayNumber],
      );
    }
    return currentlyDisplayedWeekdays.map(({ date }) => calendarItems[date]);
  }, [
    viewType,
    isProgram,
    selectedDate,
    selectedProgramDayNumber,
    selectedTagDay,
    currentlyDisplayedWeekdays,
    currentlyDisplayedProgramDays,
    calendarItems,
    nonDateBasedProgramItems,
    monthViewWindowStart,
  ]);

  /** Aggregate reps + exercise count per tag. The backend returns an empty tags array on scheduled
   * items, so we invert tagsForAccount instead: each account tag lists the exercise definitions it
   * is applied to (tag.exercises[].id), and that id matches the scheduled item's exerciseId. We
   * build an exerciseId -> [tag] map, then look up each scheduled exercise to find its tags. An
   * exercise counts toward every tag it carries. */
  const tagRows = useMemo(() => {
    const exerciseTagMap = {};
    (tagsForAccount || []).forEach((tag) => {
      if (tag?.isDeleted) return;
      (tag.exercises || []).forEach((ex) => {
        if (ex?.id == null) return;
        const exKey = String(ex.id);
        if (!exerciseTagMap[exKey]) exerciseTagMap[exKey] = [];
        exerciseTagMap[exKey].push({ tagId: tag.id, tagName: tag.name });
      });
    });

    const agg = {};
    (displayedDays || []).filter(Boolean).forEach((day) => {
      (day.exercises || []).forEach((item) => {
        const exerciseId = item?.exerciseId != null ? item.exerciseId : item?.exercise?.id;
        if (exerciseId == null) return;
        const tags = exerciseTagMap[String(exerciseId)];
        if (!tags || !tags.length) return;
        const reps = item?.repCount || 0;
        tags.forEach(({ tagId, tagName }) => {
          const key = String(tagId);
          if (!agg[key]) {
            agg[key] = {
              key, name: tagName, reps: 0, exercises: 0,
            };
          }
          agg[key].reps += reps;
          agg[key].exercises += 1;
        });
      });
    });
    return Object.values(agg);
  }, [displayedDays, tagsForAccount]);

  const sortedRows = useMemo(() => {
    const rows = [...tagRows];
    const dir = sortDirection === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sortColumn === 'name') return a.name.localeCompare(b.name) * dir;
      return (a[sortColumn] - b[sortColumn]) * dir;
    });
    return rows;
  }, [tagRows, sortColumn, sortDirection]);

  const handleSort = (columnKey) => {
    if (columnKey === sortColumn) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection(columnKey === 'name' ? 'asc' : 'desc');
    }
  };

  if (!sortedRows.length) {
    return (
      <EmptyState>
        <Text fontSize='13px' color={colors.textSecondary}>No tagged exercises to display.</Text>
      </EmptyState>
    );
  }

  return (
    <Table>
      <Row isHeader>
        {COLUMNS.map((col) => {
          const isActive = sortColumn === col.key;
          return (
            <HeaderCell
              key={col.key}
              alignEnd={col.alignEnd}
              onClick={() => handleSort(col.key)}
            >
              <Text fontSize='13px' fontWeight={600} color={colors.text}>{col.label}</Text>
              <IcomoonReact
                iconSet={iconSet}
                size={10}
                icon={isActive && sortDirection === 'asc' ? 'up-arrow' : 'down-arrow'}
                color={isActive ? colors.textSecondary : colors.textTertiary}
              />
            </HeaderCell>
          );
        })}
      </Row>
      {sortedRows.map((row) => (
        <Row key={row.key}>
          <DataCell>
            <Text fontSize='13px' fontWeight={600} color={colors.text}>{row.name}</Text>
          </DataCell>
          <DataCell alignEnd>
            <Text fontSize='13px' color={colors.textMuted}>
              <AnimatedNumber value={row.reps} />
            </Text>
          </DataCell>
          <DataCell alignEnd>
            <Text fontSize='13px' color={colors.textMuted}>
              <AnimatedNumber value={row.exercises} />
            </Text>
          </DataCell>
        </Row>
      ))}
    </Table>
  );
};

export default StatsTagBreakdown;
